// Q9 S6 — Server error → UI shape conversions + field-name routing.
//
// The API surface (apps/api/src/routes/v1/visit-report-section-handlers.ts)
// returns 422 in three different shapes depending on which gate failed:
//
//   1. Zod parse failure (per-section PATCH or full submit):
//        { error: { fieldErrors: Record<string, string[]>, formErrors: string[] } }
//      (Zod's `flatten()` output.)
//
//   2. validateCrossFields failure (cross-field rules at submit, e.g.,
//      bp_systolic ↔ bp_position; escalation_flag ↔ escalation_severity):
//        { errors: Array<{ field: string; error: string }> }
//
//   3. Generic / non-422 / no body shape:
//        { error: "string message" }
//
// `parseServerError` normalises these to a single shape the wizard
// consumes. Each fielded error carries the API field name + message;
// `routeFieldErrorsToSections` partitions them by owning section so
// the wizard can both surface inline (current section) AND navigate
// to the offending step on submit-422 (D8 8a — locked).

import {
  vitalsPatchSchema, mobilityPatchSchema, clinicalPatchSchema,
  wellbeingPatchSchema, medicationPatchSchema, notesPatchSchema,
} from "@hcm/core";
import { ApiError } from "./api.js";
import type { SectionStep } from "./visit-report-conditional-rules.js";

/** Internal normalised shape — what the wizard + components consume. */
export interface ParsedServerError {
  /** API field name → ordered messages, e.g., "bp_position" → ["..."]. */
  fieldErrors:  ReadonlyMap<string, readonly string[]>;
  /** Errors that don't anchor to a single field (Zod top-level / cross-section). */
  formErrors:   readonly string[];
  /** Generic catch-all message — used when the body has no structured errors. */
  fallback?:    string;
}

const EMPTY_PARSED: ParsedServerError = {
  fieldErrors: new Map(),
  formErrors:  [],
};

/** Shape 1 detector — Zod flatten output: { error: { fieldErrors, formErrors } } */
function tryParseZodFlatten(obj: Record<string, unknown>): ParsedServerError | null {
  if (!obj.error || typeof obj.error !== "object") return null;
  const inner = obj.error as Record<string, unknown>;
  if (!("fieldErrors" in inner || "formErrors" in inner)) return null;
  return {
    fieldErrors: toMap((inner.fieldErrors as Record<string, string[]>) ?? {}),
    formErrors:  Array.isArray(inner.formErrors) ? inner.formErrors as string[] : [],
  };
}

function extractCrossFieldEntry(entry: unknown): { field?: string; message: string } | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as { field?: unknown; error?: unknown; message?: unknown };
  const message = typeof e.error === "string" ? e.error
                : typeof e.message === "string" ? e.message
                : JSON.stringify(entry);
  return typeof e.field === "string" ? { field: e.field, message } : { message };
}

function appendParsedEntry(
  entry: { field?: string; message: string },
  fieldErrors: Map<string, string[]>, formErrors: string[],
): void {
  if (!entry.field) {
    formErrors.push(entry.message);
    return;
  }
  const existing = fieldErrors.get(entry.field);
  if (existing) existing.push(entry.message);
  else fieldErrors.set(entry.field, [entry.message]);
}

/** Shape 2 detector — validateCrossFields: { errors: [{ field, error }] } */
function tryParseCrossFieldList(obj: Record<string, unknown>): ParsedServerError | null {
  if (!Array.isArray(obj.errors)) return null;
  const fieldErrors = new Map<string, string[]>();
  const formErrors:  string[] = [];
  for (const raw of obj.errors) {
    const parsed = extractCrossFieldEntry(raw);
    if (parsed) appendParsedEntry(parsed, fieldErrors, formErrors);
  }
  return { fieldErrors, formErrors };
}

/** Convert any thrown error from the API client into our normalised shape. */
export function parseServerError(err: unknown): ParsedServerError {
  if (!(err instanceof ApiError)) {
    return { ...EMPTY_PARSED, fallback: err instanceof Error ? err.message : "Save failed" };
  }
  if (!err.body || typeof err.body !== "object") {
    return { ...EMPTY_PARSED, fallback: err.message };
  }
  const obj = err.body as Record<string, unknown>;
  return tryParseZodFlatten(obj)
      ?? tryParseCrossFieldList(obj)
      ?? { ...EMPTY_PARSED, fallback: typeof obj.error === "string" ? obj.error : err.message };
}

function toMap(record: Record<string, string[]>): ReadonlyMap<string, readonly string[]> {
  const m = new Map<string, readonly string[]>();
  for (const [k, v] of Object.entries(record)) {
    if (Array.isArray(v) && v.length > 0) m.set(k, v);
  }
  return m;
}

// ── Field-name → owning-section routing ────────────────────────────────────
//
// TASK-48 S2 — Built from the per-section Zod picks at @hcm/core (single
// source of truth). Per Q9 D22 each field belongs to exactly ONE section.
// Renaming a schema field rebuilds this map automatically.
//
// `medication_adherence_log.<n>.<field>` paths route to the medication
// section (handled with a startsWith check below); same for symptom_detail.

function buildFieldToSection(): ReadonlyMap<string, SectionStep> {
  const m = new Map<string, SectionStep>();
  const sections: ReadonlyArray<readonly [SectionStep, Record<string, unknown>]> = [
    ["vitals",     vitalsPatchSchema.shape],
    ["mobility",   mobilityPatchSchema.shape],
    ["clinical",   clinicalPatchSchema.shape],
    ["wellbeing",  wellbeingPatchSchema.shape],
    ["medication", medicationPatchSchema.shape],
    ["notes",      notesPatchSchema.shape],
  ];
  for (const [section, shape] of sections) {
    for (const field of Object.keys(shape)) m.set(field, section);
  }
  return m;
}

const FIELD_TO_SECTION: ReadonlyMap<string, SectionStep> = buildFieldToSection();

/** Returns the section that owns this field, or null if it's not a
 *  section-owned field (e.g., sr_id, completed_at, visible_steps). */
export function getFieldSection(fieldName: string): SectionStep | null {
  // Array-path: medication_adherence_log.<n>.<sub>
  if (fieldName.startsWith("medication_adherence_log")) return "medication";
  // Symptom_detail.<flag> nested path (Wellbeing).
  if (fieldName.startsWith("symptom_detail")) return "wellbeing";
  return FIELD_TO_SECTION.get(fieldName) ?? null;
}

/** Group server-side field errors by owning section. Errors for unknown
 *  fields fall under `unrouted`. */
export function routeFieldErrorsToSections(
  fieldErrors: ReadonlyMap<string, readonly string[]>,
): { bySection: Map<SectionStep, Map<string, readonly string[]>>; unrouted: Map<string, readonly string[]> } {
  const bySection = new Map<SectionStep, Map<string, readonly string[]>>();
  const unrouted  = new Map<string, readonly string[]>();
  for (const [field, msgs] of fieldErrors) {
    const section = getFieldSection(field);
    if (section) {
      const sec = bySection.get(section) ?? new Map<string, readonly string[]>();
      sec.set(field, msgs);
      bySection.set(section, sec);
    } else {
      unrouted.set(field, msgs);
    }
  }
  return { bySection, unrouted };
}
